import { useState, useEffect, useCallback } from 'react';
import { Check, Search } from 'lucide-react';
import { financeV2Service, CreateTransactionDto, PayTransactionDto } from '../../../services/financeV2Service';
import { clientService } from '../../../services/client-service';
import type { Transaction, Account } from '../../../types/financeV2.types';
import type { Client } from '../../../types/client';
import { NativeSelect } from "@/components/ui/native-select"
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'
import { AmountText } from '@/components/finance-v2/FinanceBadges'
import { cn } from '@/utils/cn'

import { getErrorMessage } from '../../../utils/error.utils'
import { parseDbTimestamp } from '../../../utils/datetime'
interface QuickReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function QuickReceiptModal({ isOpen, onClose, onSuccess }: QuickReceiptModalProps) {
  // Mode selection
  const [mode, setMode] = useState<'link' | 'adhoc'>('link'); // link = vincular a cobrança pendente, adhoc = lançamento avulso

  // Link mode (pay existing transaction)
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [paidAmount, setPaidAmount] = useState('');

  // Adhoc mode (create new receipt)
  const [clientId, setClientId] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split('T')[0]);
  const [accountId, setAccountId] = useState('');
  const [notes, setNotes] = useState('');

  // Data
  const [clients, setClients] = useState<Client[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      const [clientsRes, accountsRes] = await Promise.all([
        clientService.getAllClients({ status: 'ACTIVE' }, { page: 1, limit: 1000, sort: 'fullName:asc' }),
        financeV2Service.accounts.list({ isActive: true, limit: 100 })
      ]);

      setClients(clientsRes.clients || []);
      setAccounts(accountsRes.data.accounts || []);

      // Set default account
      if (accountsRes.data.accounts && accountsRes.data.accounts.length > 0) {
        setAccountId(accountsRes.data.accounts[0].id);
      }

    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar dados'));
    } finally {
      setLoadingData(false);
    }
  };

  const loadPendingTransactions = useCallback(async () => {
    try {
      const filters: Record<string, unknown> = {
        type: 'INCOME',
        status: 'OPEN', // em aberto: a vencer + vencidos (regra do servidor)
        limit: 20
      };

      if (searchTerm) {
        filters.search = searchTerm;
      }

      const response = await financeV2Service.transactions.list(filters);
      setPendingTransactions(response.data.transactions || []);
    } catch (err) {
      console.error('Error loading pending transactions:', err);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
    // loadInitialData usa apenas serviços módulo (estáveis); roda só ao abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (mode === 'link') {
      loadPendingTransactions();
    }
  }, [mode, loadPendingTransactions]);

  useEffect(() => {
    if (selectedTransaction) {
      setPaidAmount(selectedTransaction.amount.toString());
    }
  }, [selectedTransaction]);

  // Helper function to get client display name
  const getClientDisplayName = (client: Client): string => {
    if (client.clientType === 'PERSON') {
      return client.personalData?.fullName ||
             `${client.personalData?.firstName || ''} ${client.personalData?.lastName || ''}`.trim() ||
             'Cliente sem nome';
    } else {
      return client.companyData?.companyName ||
             client.companyData?.tradingName ||
             'Empresa sem nome';
    }
  };

  const handlePayTransaction = async () => {
    if (!selectedTransaction) {
      setError('Selecione uma cobrança pendente');
      return;
    }

    if (!paidDate || !paidAmount) {
      setError('Preencha a data e valor do pagamento');
      return;
    }

    const numAmount = parseFloat(paidAmount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Valor inválido');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payData: PayTransactionDto = {
        paidDate,
        paidAmount: numAmount,
        notes: notes || undefined
      };

      await financeV2Service.transactions.pay(selectedTransaction.id, payData);

      // Success!
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao registrar pagamento'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdhocReceipt = async () => {
    if (!description || !amount || !receiptDate || !accountId) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Valor inválido');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create the transaction already as PAID
      const transactionData: CreateTransactionDto = {
        description,
        amount: numAmount,
        type: 'INCOME',
        accountId,
        dueDate: receiptDate,
        notes: notes || undefined,
        metadata: {
          clientId: clientId || undefined,
          adhocReceipt: true,
          simplified: true
        }
      };

      // Create transaction
      const createResponse = await financeV2Service.transactions.create(transactionData);
      const transactionId = createResponse.data.transaction.id;

      // Immediately pay it
      const payData: PayTransactionDto = {
        paidDate: receiptDate,
        paidAmount: numAmount,
        notes: 'Recebimento avulso'
      };

      await financeV2Service.transactions.pay(transactionId, payData);

      // Success!
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao registrar recebimento'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'link') {
      await handlePayTransaction();
    } else {
      await handleCreateAdhocReceipt();
    }
  };

  const handleClose = () => {
    // Reset form
    setMode('link');
    setSearchTerm('');
    setSelectedTransaction(null);
    setPaidDate(new Date().toISOString().split('T')[0]);
    setPaidAmount('');
    setClientId('');
    setDescription('');
    setAmount('');
    setReceiptDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) handleClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Registrar Recebimento</DialogTitle>
          <DialogDescription>Baixa de uma cobrança em aberto ou recebimento avulso.</DialogDescription>
        </DialogHeader>

        {loadingData ? (
          <div className="flex flex-col gap-4" aria-busy="true">
            <span className="sr-only">Carregando dados...</span>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : (
          <form id="quick-receipt-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
            {error && (
              <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <fieldset className="flex min-w-0 flex-col gap-2">
              <legend className="mb-1.5 text-sm font-medium">Tipo de Recebimento</legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <ModeOption
                  selected={mode === 'link'}
                  onSelect={() => setMode('link')}
                  title="Cobrança Existente"
                  description="Dar baixa em cobrança pendente"
                />
                <ModeOption
                  selected={mode === 'adhoc'}
                  onSelect={() => setMode('adhoc')}
                  title="Recebimento Avulso"
                  description="Registrar novo recebimento"
                />
              </div>
            </fieldset>

            {mode === 'link' && (
              <>
                <FormField label="Buscar Cobrança Pendente" htmlFor="receipt-search">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                    <Input
                      id="receipt-search"
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar por descrição ou cliente..."
                      className="pl-8"
                    />
                  </div>
                </FormField>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <p id="receipt-pending-label" className="text-sm font-medium">Selecione a Cobrança</p>
                  <div className="max-h-64 overflow-y-auto rounded-lg border" role="group" aria-labelledby="receipt-pending-label">
                    {pendingTransactions.length === 0 ? (
                      <p className="p-6 text-center text-sm text-muted-foreground">
                        Nenhuma cobrança pendente encontrada
                      </p>
                    ) : (
                      <div className="divide-y">
                        {pendingTransactions.map((transaction) => {
                          const selected = selectedTransaction?.id === transaction.id;
                          return (
                            <button
                              key={transaction.id}
                              type="button"
                              aria-pressed={selected}
                              onClick={() => setSelectedTransaction(transaction)}
                              className={cn(
                                'flex w-full items-center justify-between gap-3 border-l-2 border-transparent p-3 text-left transition-colors hover:bg-muted',
                                selected && 'border-l-primary bg-primary/5'
                              )}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="wrap-break-word font-medium">{transaction.description}</div>
                                <div className="mt-1 text-xs text-muted-foreground">
                                  Vencimento: {parseDbTimestamp(transaction.dueDate)?.toLocaleDateString('pt-BR') ?? '-'}
                                </div>
                              </div>
                              <div className="flex shrink-0 flex-col items-end gap-1">
                                <AmountText tone="income" className="font-semibold">
                                  R$ {transaction.amount.toFixed(2)}
                                </AmountText>
                                {selected && <Check className="size-4 text-primary" aria-hidden="true" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {selectedTransaction && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField label="Data do Pagamento *" htmlFor="receipt-paid-date">
                      <Input
                        id="receipt-paid-date"
                        type="date"
                        value={paidDate}
                        onChange={(e) => setPaidDate(e.target.value)}
                        required
                      />
                    </FormField>

                    <FormField
                      label="Valor Recebido *"
                      htmlFor="receipt-paid-amount"
                      hint={`Valor original: R$ ${selectedTransaction.amount.toFixed(2)}`}
                    >
                      <CurrencyInput id="receipt-paid-amount" value={paidAmount} onChange={setPaidAmount} />
                    </FormField>
                  </div>
                )}
              </>
            )}

            {mode === 'adhoc' && (
              <>
                <FormField label="Cliente (opcional)" htmlFor="receipt-client">
                  <NativeSelect
                    id="receipt-client"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                  >
                    <option value="">Sem cliente vinculado</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {getClientDisplayName(client)}
                      </option>
                    ))}
                  </NativeSelect>
                </FormField>

                <FormField label="Descrição *" htmlFor="receipt-description">
                  <Input
                    id="receipt-description"
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ex: Recebimento de honorários avulso"
                    required
                  />
                </FormField>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField label="Valor Recebido *" htmlFor="receipt-amount">
                    <CurrencyInput id="receipt-amount" value={amount} onChange={setAmount} placeholder="0,00" />
                  </FormField>

                  <FormField label="Data do Recebimento *" htmlFor="receipt-date">
                    <Input
                      id="receipt-date"
                      type="date"
                      value={receiptDate}
                      onChange={(e) => setReceiptDate(e.target.value)}
                      required
                    />
                  </FormField>
                </div>

                <FormField label="Conta *" htmlFor="receipt-account">
                  <NativeSelect
                    id="receipt-account"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    required
                  >
                    <option value="">Selecione uma conta</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({account.type})
                      </option>
                    ))}
                  </NativeSelect>
                </FormField>
              </>
            )}

            {/* Observações (comum para ambos os modos) */}
            <FormField label="Observações" htmlFor="receipt-notes">
              <Textarea
                id="receipt-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Informações adicionais sobre este recebimento..."
                className="resize-none"
              />
            </FormField>
          </form>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="quick-receipt-form"
            loading={loading}
            disabled={loadingData || (mode === 'link' && !selectedTransaction)}
          >
            {loading ? 'Registrando...' : <><Check className="size-4" /> Registrar Recebimento</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ModeOptionProps {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}

/** Botão de escolha do tipo de recebimento (cobrança existente / avulso). */
function ModeOption({ selected, onSelect, title, description }: ModeOptionProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted',
        selected && 'border-primary bg-primary/5 hover:bg-primary/10'
      )}
    >
      <div className="text-sm font-medium">{title}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
    </button>
  );
}

interface CurrencyInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

function CurrencyInput({ id, value, onChange, placeholder }: CurrencyInputProps) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
      <Input
        id={id}
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9"
        required
      />
    </div>
  );
}
